import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFileUpload } from '../../hooks/useFileUpload';

const {
  FiX, FiMusic, FiLoader, FiDollarSign, FiUploadCloud,
  FiImage, FiHardDrive, FiDroplet, FiLink, FiCheck,
  FiFile, FiCloud, FiAlertCircle, FiRefreshCw,
} = FiIcons;

const inputCls = 'w-full bg-zinc-900/60 border border-white/8 rounded-xl px-4 py-3 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-brand-pink/40 transition-colors';
const GENRES = ['Electronic', 'Hip-Hop', 'R&B', 'Pop', 'Rock', 'Jazz', 'Classical', 'Ambient', 'House', 'Techno', 'Drum & Bass', 'Other'];
const CLOUD_SERVICES = [
  { id: 'gdrive',   label: 'Google Drive',  icon: FiCloud,      color: 'text-blue-400',   placeholder: 'https://drive.google.com/file/...' },
  { id: 'dropbox',  label: 'Dropbox',       icon: FiDroplet,    color: 'text-blue-500',   placeholder: 'https://www.dropbox.com/s/...'    },
  { id: 'onedrive', label: 'OneDrive',      icon: FiCloud,      color: 'text-sky-400',    placeholder: 'https://onedrive.live.com/...'    },
  { id: 'url',      label: 'Direct URL',    icon: FiLink,       color: 'text-zinc-400',   placeholder: 'https://...'                      },
];

const Field = ({ label, hint, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</label>
      {hint && <span className="text-[10px] text-zinc-700 font-bold">{hint}</span>}
    </div>
    {children}
  </div>
);

/* ── File Drop Zone ── */
const FileDropZone = ({ label, accept, icon, onChange, hint, artistId, bucket, folder }) => {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [localFile, setLocalFile] = useState(null);
  const [localUrl, setLocalUrl] = useState('');
  const [uploadState, setUploadState] = useState('idle'); // 'idle', 'uploading', 'success', 'error'
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const { upload, uploading, progress, error, url, reset } = useFileUpload(
    bucket || 'drops', 
    `${artistId}/${folder || 'files'}`
  );

  // Sync internal state with hook state
  useEffect(() => {
    setUploadProgress(progress);
    if (uploading) {
      setUploadState('uploading');
    } else if (error) {
      setUploadState('error');
    } else if (url) {
      setUploadState('success');
      onChange(url, null); // Success - pass URL but no file (already uploaded)
    }
  }, [uploading, progress, error, url, onChange]);

  const handleFile = useCallback((file) => {
    if (!file) return;
    setLocalFile(file);
    setFileName(file.name);
    const objectUrl = URL.createObjectURL(file);
    setLocalUrl(objectUrl);
    onChange(objectUrl, file);
    setUploadState('idle');
  }, [onChange]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const handleUpload = async () => {
    if (!localFile) return;
    setUploadState('uploading');
    setUploadProgress(0);
    reset();
    
    // Upload the file
    await upload(localFile);
  };

  // Auto-upload when file is selected if artistId is provided
  useEffect(() => {
    if (localFile && artistId && uploadState === 'idle') {
      handleUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFile, artistId]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [localUrl]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => ref.current?.click()}
      className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all p-6 text-center space-y-3 ${
        uploadState === 'error' ? 'border-red-500/40 bg-red-500/5' :
        uploadState === 'uploading' ? 'border-amber-500/40 bg-amber-500/5' :
        uploadState === 'success' ? 'border-green-500/40 bg-green-500/5' :
        dragging ? 'border-brand-pink bg-brand-pink/5' :
        fileName ? 'border-brand-pink/40 bg-brand-pink/5' :
        'border-white/10 hover:border-brand-pink/30 hover:bg-brand-pink/5'
      }`}>
      <input 
        ref={ref} 
        type="file" 
        accept={accept} 
        className="hidden"
        onChange={e => handleFile(e.target.files[0])} 
      />

      {uploadState === 'error' ? (
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <SafeIcon icon={FiAlertCircle} className="text-red-400 text-lg" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-black text-white truncate max-w-[250px]">{fileName}</p>
            <p className="text-[10px] text-red-400 font-black uppercase tracking-widest">Upload failed</p>
            <button 
              onClick={(e) => { e.stopPropagation(); handleUpload(); }} 
              className="mt-2 px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10"
            >
              <SafeIcon icon={FiRefreshCw} className="inline-block mr-1" /> Retry
            </button>
          </div>
        </div>
      ) : uploadState === 'uploading' ? (
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
            <SafeIcon icon={FiLoader} className="text-amber-400 text-lg animate-spin" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-black text-white truncate max-w-[250px]">{fileName}</p>
            <p className="text-[10px] text-amber-400 font-black uppercase tracking-widest">Uploading {uploadProgress.toFixed(0)}%</p>
            <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden mt-2">
              <div className="h-full bg-amber-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        </div>
      ) : uploadState === 'success' ? (
        <div className="flex items-center justify-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
            <SafeIcon icon={FiCheck} className="text-green-400 text-lg" />
          </div>
          <div className="text-left">
            <p className="text-sm font-black text-white truncate max-w-[200px]">{fileName}</p>
            <p className="text-[10px] text-green-400 font-black uppercase tracking-widest">Upload complete</p>
          </div>
        </div>
      ) : fileName ? (
        <div className="flex items-center justify-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-pink/10 border border-brand-pink/20 flex items-center justify-center">
            <SafeIcon icon={icon} className="text-brand-pink text-lg" />
          </div>
          <div className="text-left">
            <p className="text-sm font-black text-white truncate max-w-[200px]">{fileName}</p>
            <p className="text-[10px] text-brand-pink font-black uppercase tracking-widest">File selected</p>
          </div>
        </div>
      ) : (
        <>
          <div className="w-12 h-12 rounded-2xl glass-card flex items-center justify-center mx-auto">
            <SafeIcon icon={icon} className="text-2xl text-zinc-500" />
          </div>
          <div>
            <p className="text-sm font-black text-white">{label}</p>
            <p className="text-[10px] font-bold text-zinc-600 mt-1">{hint}</p>
          </div>
          <p className="text-[10px] text-zinc-700 font-bold">Drag &amp; drop or click to browse</p>
        </>
      )}
    </div>
  );
};

/* ── Cloud / URL Source Picker ── */
const CloudSourcePicker = ({ value, onChange, placeholder }) => {
  const [selected, setSelected] = useState('url');
  const service = CLOUD_SERVICES.find(s => s.id === selected);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2">
        {CLOUD_SERVICES.map(s => (
          <button key={s.id} type="button" onClick={() => setSelected(s.id)}
            className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all ${
              selected === s.id
                ? 'border-brand-pink/40 bg-brand-pink/5'
                : 'border-white/8 hover:border-white/15 hover:bg-white/5'
            }`}>
            <SafeIcon icon={s.icon} className={`text-lg ${selected === s.id ? s.color : 'text-zinc-600'}`} />
            <span className={`text-[9px] font-black uppercase tracking-wider leading-tight ${selected === s.id ? 'text-white' : 'text-zinc-600'}`}>
              {s.label}
            </span>
          </button>
        ))}
      </div>
      <div className="relative">
        <SafeIcon icon={service.icon} className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${service.color}`} />
        <input className={`${inputCls} pl-10`}
          placeholder={service.placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      </div>
    </div>
  );
};

/* ── Source Mode Toggle ── */
const SourceToggle = ({ mode, onChange }) => (
  <div className="flex gap-1.5 bg-zinc-900/60 border border-white/8 rounded-2xl p-1.5">
    {[
      { id: 'device', label: 'From Device', icon: FiHardDrive },
      { id: 'cloud',  label: 'Cloud / URL', icon: FiCloud },
    ].map(({ id, label, icon }) => (
      <button key={id} type="button" onClick={() => onChange(id)}
        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
          mode === id ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'
        }`}>
        <SafeIcon icon={icon} className="text-sm" /> {label}
      </button>
    ))}
  </div>
);

/* ── Main Modal ── */
const UploadDropModal = ({ onClose, onCreate, artistId }) => {
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');

  const [audioMode,   setAudioMode]   = useState('device');
  const [coverMode,   setCoverMode]   = useState('device');
  const [previewMode, setPreviewMode] = useState('device');

  const [form, setForm] = useState({
    title: '', description: '', genre: '', tags: '',
    audio_url: '', preview_url: '', cover_url: '',
    min_price: '0', suggested_price: '5',
    is_published: false,
  });

  const [files, setFiles] = useState({
    audio: null,
    cover: null,
    preview: null,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAudioChange = (url, file) => {
    set('audio_url', url);
    if (file) setFiles(f => ({ ...f, audio: file }));
  };

  const handleCoverChange = (url, file) => {
    set('cover_url', url);
    if (file) setFiles(f => ({ ...f, cover: file }));
  };

  const handlePreviewChange = (url, file) => {
    set('preview_url', url);
    if (file) setFiles(f => ({ ...f, preview: file }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim())     { setErr('Title is required.'); return; }
    if (!form.audio_url.trim()) { setErr('Audio file or URL is required.'); return; }
    setLoading(true); setErr('');
    
    try {
      const { error } = await onCreate(
        {
          ...form,
          min_price: parseFloat(form.min_price) || 0,
          suggested_price: parseFloat(form.suggested_price) || 5,
          tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        },
        files
      );
      
      if (error) {
        console.error('Drop creation error:', error);
        setErr(error.message || 'Failed to create drop');
      } else {
        onClose();
      }
    } catch (e) {
      console.error('Unexpected drop creation error:', e);
      setErr(e.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-xl modal-glass rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-white/5">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-pink">Step {step} of 2</p>
            <h3 className="text-2xl font-black text-white uppercase tracking-tight">
              {step === 1 ? 'Drop Details' : 'Paywall & Pricing'}
            </h3>
          </div>
          <button onClick={onClose} className="w-10 h-10 glass-card rounded-2xl flex items-center justify-center text-zinc-400 hover:text-white transition-all">
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-zinc-900">
          <div className={`h-full bg-gradient-to-r from-brand-pink to-brand-coral transition-all duration-500 ${step === 1 ? 'w-1/2' : 'w-full'}`} />
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-8 space-y-6">
          {step === 1 && (
            <>
              <Field label="Title *">
                <input className={inputCls} placeholder="Track or EP title…" value={form.title} onChange={e => set('title', e.target.value)} />
              </Field>

              <Field label="Description">
                <textarea className={`${inputCls} resize-none`} rows={3} placeholder="Tell fans what this is…"
                  value={form.description} onChange={e => set('description', e.target.value)} />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Genre">
                  <select className={inputCls} value={form.genre} onChange={e => set('genre', e.target.value)}>
                    <option value="">Select genre…</option>
                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Tags" hint="comma separated">
                  <input className={inputCls} placeholder="dark, lo-fi, ambient" value={form.tags} onChange={e => set('tags', e.target.value)} />
                </Field>
              </div>

              {/* Cover Art */}
              <Field label="Cover Art">
                <SourceToggle mode={coverMode} onChange={setCoverMode} />
                {coverMode === 'device' ? (
                  <FileDropZone
                    label="Drop your cover art here"
                    accept="image/*"
                    icon={FiImage}
                    onChange={handleCoverChange}
                    hint="JPG, PNG, WEBP · Max 10 MB"
                    artistId={artistId}
                    bucket="drops"
                    folder="covers"
                  />
                ) : (
                  <CloudSourcePicker value={form.cover_url} onChange={v => set('cover_url', v)} />
                )}
                {form.cover_url && coverMode === 'cloud' && (
                  <div className="w-20 h-20 rounded-2xl overflow-hidden border border-white/10 mt-2">
                    <img src={form.cover_url} alt="Cover preview" className="w-full h-full object-cover" 
                      onError={e => { e.target.style.display='none'; set('cover_url', ''); }} />
                  </div>
                )}
              </Field>

              {/* Audio File */}
              <Field label="Audio File *" hint="mp3, wav, flac, aiff">
                <SourceToggle mode={audioMode} onChange={setAudioMode} />
                {audioMode === 'device' ? (
                  <FileDropZone
                    label="Drop your audio file here"
                    accept="audio/*,.flac,.aiff,.wav,.mp3"
                    icon={FiMusic}
                    onChange={handleAudioChange}
                    hint="Full-quality audio · MP3, WAV, FLAC, AIFF"
                    artistId={artistId}
                    bucket="drops"
                    folder="audio"
                  />
                ) : (
                  <CloudSourcePicker value={form.audio_url} onChange={v => set('audio_url', v)} />
                )}
              </Field>

              {/* Preview Clip */}
              <Field label="Preview Clip" hint="30s clip shown before unlock">
                <SourceToggle mode={previewMode} onChange={setPreviewMode} />
                {previewMode === 'device' ? (
                  <FileDropZone
                    label="Drop a short preview clip"
                    accept="audio/*"
                    icon={FiFile}
                    onChange={handlePreviewChange}
                    hint="Keep it under 60 seconds"
                    artistId={artistId}
                    bucket="drops"
                    folder="previews"
                  />
                ) : (
                  <CloudSourcePicker value={form.preview_url} onChange={v => set('preview_url', v)} />
                )}
              </Field>

              {err && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <SafeIcon icon={FiAlertCircle} className="text-red-400 text-sm" />
                  <p className="text-xs font-bold text-red-400">{err}</p>
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="p-5 bg-brand-pink/5 border border-brand-pink/15 rounded-2xl space-y-1">
                <p className="text-xs font-black uppercase tracking-widest text-brand-pink">Pay What You Want</p>
                <p className="text-sm font-bold text-zinc-400">Fans choose their price. You set the floor and suggestion.</p>
              </div>

              <Field label="Minimum Price (USD)" hint="Set 0 for free">
                <div className="relative">
                  <SafeIcon icon={FiDollarSign} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                  <input type="number" min="0" step="0.01" className={`${inputCls} pl-10`}
                    placeholder="0.00" value={form.min_price} onChange={e => set('min_price', e.target.value)} />
                </div>
                <p className="text-[10px] text-zinc-700 font-bold">Fans cannot pay below this amount</p>
              </Field>

              <Field label="Suggested Price (USD)">
                <div className="relative">
                  <SafeIcon icon={FiDollarSign} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                  <input type="number" min="0" step="0.01" className={`${inputCls} pl-10`}
                    placeholder="5.00" value={form.suggested_price} onChange={e => set('suggested_price', e.target.value)} />
                </div>
              </Field>

              {/* Preview card */}
              <div className="p-5 glass-card rounded-2xl space-y-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Drop Preview</p>
                <div className="flex items-center gap-4">
                  {form.cover_url && (
                    <div className="w-14 h-14 rounded-xl overflow-hidden border border-white/10 flex-shrink-0">
                      <img src={form.cover_url} alt="Cover" className="w-full h-full object-cover" 
                        onError={e => e.target.style.display='none'} />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-black text-white uppercase tracking-tight">{form.title || 'Untitled Drop'}</p>
                    <p className="text-xs font-bold text-zinc-500 mt-0.5">{form.genre || 'No genre'}</p>
                  </div>
                  <span className="text-sm font-black text-brand-pink">
                    {parseFloat(form.min_price) === 0 ? 'Free+' : `$${parseFloat(form.min_price).toFixed(2)}+`}
                  </span>
                </div>
                <p className="text-xs font-bold text-zinc-600">Suggested: ${parseFloat(form.suggested_price || 5).toFixed(2)}</p>
              </div>

              {/* Publish toggle */}
              <div className="flex items-center gap-4 p-4 glass-card rounded-2xl">
                <button type="button" onClick={() => set('is_published', !form.is_published)}
                  className={`w-12 h-6 rounded-full transition-all flex items-center px-1 flex-shrink-0 ${
                    form.is_published
                      ? 'bg-gradient-to-r from-brand-pink to-brand-coral justify-end'
                      : 'bg-zinc-800 justify-start'
                  }`}>
                  <div className="w-4 h-4 rounded-full bg-white shadow" />
                </button>
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-white">
                    {form.is_published ? 'Publish Now' : 'Save as Draft'}
                  </p>
                  <p className="text-[10px] font-bold text-zinc-600">
                    {form.is_published ? 'Fans can discover and unlock this drop immediately' : 'Only visible to you'}
                  </p>
                </div>
              </div>

              {err && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <SafeIcon icon={FiAlertCircle} className="text-red-400 text-sm" />
                  <p className="text-xs font-bold text-red-400">{err}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-5 border-t border-white/5">
          <button onClick={step === 1 ? onClose : () => { setStep(1); setErr(''); }}
            className="px-5 py-3 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all">
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          {step === 1 ? (
            <button onClick={() => { if (!form.title.trim()) { setErr('Title required'); return; } setErr(''); setStep(2); }}
              className="px-6 py-3 btn-brand rounded-2xl text-xs">
              Next →
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading}
              className="flex items-center gap-2 px-6 py-3 btn-brand rounded-2xl text-xs disabled:opacity-50">
              {loading && <SafeIcon icon={FiLoader} className="animate-spin text-sm" />}
              {form.is_published ? 'Publish Drop' : 'Save Draft'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadDropModal;